import React, { useEffect, useState, useCallback } from "react";
import { api, formatUSD } from "../../../lib/api";
import { toast } from "sonner";
import { Pause, CheckCircle, Printer, Trash } from "@phosphor-icons/react";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../../components/ui/dialog";
import { buildAgentPrintableReport } from "./agentPrintableReport";

/**
 * AgentDetailDialog — admin-only deep-dive on a single agent. Surfaces the
 * commission rollups, bank-for-payout, suspend control, and mark-all-paid
 * action. Powers the "Detail" button in the legacy Commission table on
 * /admin/agents.
 *
 * Props:
 *   - agentId: agent id to load
 *   - onClose: close-callback (parent unmounts the dialog)
 *   - onChanged: fired after any mutation (mark-paid or suspend) so the
 *     parent list refreshes
 */
export function AgentDetailDialog({ agentId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [payRef, setPayRef] = useState("");
  const [payNote, setPayNote] = useState("");

  const load = useCallback(async () => {
    const [d, c] = await Promise.all([
      api.get(`/admin/agents/${agentId}`),
      api.get(`/admin/agents/${agentId}/commissions?limit=200`),
    ]);
    setData(d.data);
    setRows(c.data.rows || []);
  }, [agentId]);

  useEffect(() => { load(); }, [load]);

  const markAllPaid = async () => {
    if (!window.confirm("Mark ALL unpaid commissions as paid? This cannot be undone.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/agents/${agentId}/commissions/mark-paid`, {
        mark_all_unpaid: true, paid_ref: payRef, paid_note: payNote,
      });
      toast.success(`Marked ${r.data.marked_paid} commission rows as paid`);
      setPayRef(""); setPayNote("");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to mark paid");
    } finally { setBusy(false); }
  };

  const suspend = async () => {
    if (!window.confirm(`Suspend agent ${data?.full_name}? They won't be able to log in or issue new cards.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/agents/${agentId}`);
      toast.success("Agent suspended");
      onClose?.();
      onChanged?.();
    } catch (err) {
      toast.error("Failed to suspend");
    } finally { setBusy(false); }
  };

  const deleteCommission = async (row) => {
    const tail = row.status === "paid" ? " This row was already marked PAID — make sure your bank ledger is reconciled." : "";
    if (!window.confirm(`Delete commission of ${formatUSD(row.commission_amount)} for ${row.customer_email}?${tail}\n\nThis hides the row from reports. The DB row is kept for audit.`)) return;
    try {
      await api.delete(`/admin/agents/${agentId}/commissions/${row.id}`);
      toast.success("Commission row deleted");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete commission");
    }
  };

  const printReport = () => {
    if (!data) return;
    const res = buildAgentPrintableReport(data, rows);
    if (res?.popupBlocked) {
      toast.warning("Pop-up blocked — opening report in current tab instead. Allow pop-ups for this site to keep it in a new tab.");
    }
  };

  if (!data) return null;
  const c = data.commissions || {};

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#FBF9F6] border-[#E5E0D8] max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="agent-detail-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <DialogTitle className="serif text-3xl">
                {data.full_name} <span className="mono text-base text-[#4A5C50]">· {data.agent_code}</span>
              </DialogTitle>
              <DialogDescription className="text-[#4A5C50]">{data.email} · {data.phone || "—"}</DialogDescription>
            </div>
            <Button
              onClick={printReport}
              variant="outline"
              size="sm"
              className="shrink-0"
              data-testid="agent-print-report-btn"
              title="Open a printable Week / Month / Year commission report in a new tab"
            >
              <Printer size={14} className="mr-1.5" /> Print report
            </Button>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-4 gap-3 mt-2">
          <DetailStat label="Week" value={formatUSD(c.week?.total ?? 0)} />
          <DetailStat label="Month" value={formatUSD(c.month?.total ?? 0)} />
          <DetailStat label="Year" value={formatUSD(c.year?.total ?? 0)} />
          <DetailStat label="Unpaid" value={formatUSD(c.unpaid?.total ?? 0)} tone="amber" />
        </div>

        <div className="bg-white rounded-xl border border-[#E5E0D8] p-4 mt-4">
          <p className="text-xs tracking-[0.2em] uppercase text-[#4A5C50]">Bank for payout</p>
          <p className="mt-1">
            <span className="font-semibold">{data.bank_name || "—"}</span>
            {" · "}<span className="mono">{data.bank_account_holder || "—"}</span>
            {" · "}<span className="mono">{data.bank_account_number ? `•••• ${data.bank_account_number.slice(-4)}` : "—"}</span>
            {data.routing_number && <> · routing <span className="mono">{data.routing_number}</span></>}
          </p>
          <p className="text-xs text-[#4A5C50] mt-1">{data.address || "—"}</p>
        </div>

        <div className="bg-white rounded-xl border border-[#E5E0D8] p-4 mt-3" data-testid="mark-paid-panel">
          <p className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] mb-2">Record payout · marks all unpaid as paid</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Bank transfer reference</Label>
              <input value={payRef} onChange={e => setPayRef(e.target.value)} className="underline-input mono" placeholder="ACH-2026-W23-001" data-testid="mark-paid-ref" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <input value={payNote} onChange={e => setPayNote(e.target.value)} className="underline-input" placeholder="Weekly batch payout" data-testid="mark-paid-note" />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <Button onClick={markAllPaid} disabled={busy || (c.unpaid?.total ?? 0) <= 0} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="mark-paid-submit">
              <CheckCircle size={14} className="mr-1" /> Mark all unpaid as paid ({formatUSD(c.unpaid?.total ?? 0)})
            </Button>
            {data.status !== "suspended" && (
              <Button onClick={suspend} variant="outline" className="text-rose-700" data-testid="suspend-agent-btn">
                <Pause size={14} className="mr-1" /> Suspend agent
              </Button>
            )}
          </div>
        </div>

        <h3 className="serif text-2xl mt-6">Commission rows ({rows.length})</h3>
        <div className="bg-white border border-[#E5E0D8] rounded-xl overflow-x-auto mt-3" data-testid="commission-rows-table">
          <table className="w-full text-sm min-w-[1000px]">
            <thead className="text-left text-[#4A5C50] text-[11px] uppercase tracking-widest">
              <tr>
                <th className="p-3">Earned</th>
                <th className="p-3">Receiver</th>
                <th className="p-3">Customer</th>
                <th className="p-3">Type</th>
                <th className="p-3 text-right">Txn $</th>
                <th className="p-3 text-right">Commission</th>
                <th className="p-3">Status</th>
                <th className="p-3">Paid ref</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={9} className="p-6 text-center text-[#4A5C50]">No commission rows yet.</td></tr>
              ) : rows.map(r => (
                <tr key={r.id} className="border-t border-[#E5E0D8]" data-testid={`commission-row-${r.id}`}>
                  <td className="p-3 text-xs">{new Date(r.earned_at).toLocaleString()}</td>
                  <td className="p-3">
                    {(r.receiver_name || r.receiver_email || r.receiver_phone) ? (
                      <>
                        {r.receiver_name && <div className="text-[13px] font-semibold text-[#112318]">{r.receiver_name}</div>}
                        {r.receiver_email && <div className="text-[11px] mono text-[#4A5C50]">{r.receiver_email}</div>}
                        {r.receiver_phone && <div className="text-[11px] mono text-[#4A5C50]">{r.receiver_phone}</div>}
                      </>
                    ) : (
                      <span className="text-[#C7C0B4] text-xs">—</span>
                    )}
                  </td>
                  <td className="p-3 mono text-xs">{r.customer_email}</td>
                  <td className="p-3">{r.transaction_type}</td>
                  <td className="p-3 text-right mono">{formatUSD(r.transaction_amount)}</td>
                  <td className="p-3 text-right mono font-semibold text-emerald-700">{formatUSD(r.commission_amount)}</td>
                  <td className="p-3">
                    {r.status === "paid"
                      ? <span className="text-emerald-700 text-xs uppercase tracking-widest">Paid</span>
                      : <span className="text-amber-700 text-xs uppercase tracking-widest">Unpaid</span>}
                  </td>
                  <td className="p-3 mono text-xs text-[#4A5C50]">{r.paid_ref || "—"}</td>
                  <td className="p-3 text-right">
                    <button
                      type="button"
                      onClick={() => deleteCommission(r)}
                      className="text-rose-700 hover:text-rose-900 transition"
                      title="Delete this commission row (hides from reports; kept for audit)"
                      data-testid={`commission-delete-${r.id}`}
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-[#FBF9F6] border-t-2 border-[#184A30]" data-testid="commission-total-row">
                <tr>
                  <td colSpan={4} className="p-3 text-xs uppercase tracking-widest text-[#4A5C50] font-semibold">
                    Total · {rows.length} {rows.length === 1 ? "row" : "rows"}
                  </td>
                  <td className="p-3 text-right mono text-xs text-[#4A5C50]">
                    {formatUSD(rows.reduce((s, r) => s + (Number(r.transaction_amount) || 0), 0))}
                  </td>
                  <td className="p-3 text-right mono text-base font-bold text-[#184A30]" data-testid="commission-grand-total">
                    {formatUSD(rows.reduce((s, r) => s + (Number(r.commission_amount) || 0), 0))}
                  </td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <h3 className="serif text-2xl mt-6">Customers ({(data.customers || []).length})</h3>
        <div className="bg-white border border-[#E5E0D8] rounded-xl overflow-hidden mt-3" data-testid="agent-customers-list">
          {(data.customers || []).length === 0 ? (
            <div className="p-6 text-center text-[#4A5C50] text-sm">No customers yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-[#4A5C50] text-[11px] uppercase tracking-widest">
                <tr><th className="p-3">Customer</th><th className="p-3">Email</th><th className="p-3">Joined</th></tr>
              </thead>
              <tbody>
                {(data.customers || []).map(u => (
                  <tr key={u.id} className="border-t border-[#E5E0D8]">
                    <td className="p-3 font-semibold">{u.full_name}</td>
                    <td className="p-3 mono text-xs">{u.email}</td>
                    <td className="p-3 text-xs text-[#4A5C50]">{new Date(u.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DetailStat({ label, value, tone }) {
  const cls = tone === "amber" ? "text-amber-700" : "text-[#112318]";
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-xl p-3 text-center">
      <p className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">{label}</p>
      <p className={`serif text-2xl ${cls} mt-1`}>{value}</p>
    </div>
  );
}
